import { toBrazilianDate } from '../../../../helpers/utils/utils';
import { formatBRL } from '../../UI';

const CAMPOS_MONETARIOS = [
  'valorOriginal', 'saldo', 'valorProtesto', 'juros', 'total',
  'valorMulta', 'valorHonorarios', 'valorDesconto',
];

/**
 * Colunas do modo "padrão" — ordem exata do texto do AC do Jira (AW2-1034):
 * "Seq., Tipo, Número, Vencimento, Atraso, Valor Original, Saldo, Protesto,
 * Valor Protesto, Juros, Total, Status" — Status por último, diferente da
 * posição na tela real (`Titulos.jsx`, onde Status é a 2ª coluna). Decisão
 * já tomada na sessão de arquitetura: seguir o AC literalmente para este
 * modelo de grid (botão único "Expandir Títulos"), não a tela real.
 *
 * `ordenarPor` (AW2-1034 Etapa 2) é o nome do campo do DTO usado no
 * parâmetro de sort enviado ao backend — distinto do `campo` de UI (usado
 * para mapear/exibir o valor) sempre que os dois nomes divergem (ex.: UI
 * `saldo` → DTO `valorSaldoPrincipal`). `null` marca as colunas sem
 * `OrdenacaoColuna` na tela real (Protesto, Valor Protesto, Juros, Total —
 * 2 delas nem existem como campo real, são calculadas; as outras 2 só não
 * têm ordenação na tela real apesar de existirem no DTO) e a coluna `acoes`
 * (AW2-1034 Etapa 4 — nunca ordenável, não é um campo de dado).
 */
export const COLUNAS_PADRAO = [
  { campo: 'sequencia', label: 'Seq.', ordenarPor: 'sequencia' },
  { campo: 'tipo', label: 'Tipo', ordenarPor: 'descricaoTipoTitulo' },
  { campo: 'numero', label: 'Número', ordenarPor: 'numero' },
  { campo: 'vencimento', label: 'Vencimento', ordenarPor: 'dataVencimento' },
  {
    campo: 'atraso', label: 'Atraso', align: 'center', ordenarPor: 'atraso',
  },
  {
    campo: 'valorOriginal', label: 'Valor Original', align: 'right', ordenarPor: 'valorOriginal',
  },
  {
    campo: 'saldo', label: 'Saldo', align: 'right', ordenarPor: 'valorSaldoPrincipal',
  },
  {
    campo: 'protestado', label: 'Protesto', align: 'center', ordenarPor: null,
  },
  {
    campo: 'valorProtesto', label: 'Valor Protesto', align: 'right', ordenarPor: null,
  },
  {
    campo: 'juros', label: 'Juros', align: 'right', ordenarPor: null,
  },
  {
    campo: 'total', label: 'Total', align: 'right', ordenarPor: null,
  },
  { campo: 'status', label: 'Status', ordenarPor: 'descricaoStatusTitulo' },
  { campo: 'acoes', label: 'Ações', ordenarPor: null },
];

/**
 * Colunas do modo "detalhada" (AW2-1034 Etapa 3) — 13 colunas, alternadas
 * pelo botão único "Expandir/Recolher Títulos" (decisão de arquitetura já
 * tomada: toggle global, não o acordeão por linha da tela real). Confirmado
 * com o usuário nesta etapa (o AC do Jira não especifica a composição
 * exata): remove as 2 colunas do modo padrão que o acordeão real não expõe
 * como tal — `protestado` (indicador Sim/Não) e `total` (calculado, já
 * coberto pelo card "Cálculo do Processo") — e troca `valorOriginal`/`saldo`
 * para os rótulos "Valor Principal"/"Saldo do Principal"; `valorProtesto`
 * perde o prefixo "Valor" (rótulo "Protesto", mesmo campo do modo padrão);
 * adiciona `valorMulta`/`valorHonorarios`/`valorDesconto` (só existem aqui),
 * na mesma ordem do bloco de detalhe do acordeão real
 * (`TabelaPesquisaTitulos.jsx`: Protesto, Multa, Juros, Honorários,
 * Descontos). Nenhuma das 3 colunas novas tem `OrdenacaoColuna` na tela
 * real — `ordenarPor: null`, mesmo critério já aplicado às colunas
 * exclusivas do modo padrão.
 */
export const COLUNAS_DETALHADA = [
  { campo: 'sequencia', label: 'Seq.', ordenarPor: 'sequencia' },
  { campo: 'tipo', label: 'Tipo', ordenarPor: 'descricaoTipoTitulo' },
  { campo: 'numero', label: 'Número', ordenarPor: 'numero' },
  { campo: 'vencimento', label: 'Vencimento', ordenarPor: 'dataVencimento' },
  {
    campo: 'atraso', label: 'Atraso', align: 'center', ordenarPor: 'atraso',
  },
  {
    campo: 'valorOriginal', label: 'Valor Principal', align: 'right', ordenarPor: 'valorOriginal',
  },
  {
    campo: 'saldo', label: 'Saldo do Principal', align: 'right', ordenarPor: 'valorSaldoPrincipal',
  },
  {
    campo: 'valorProtesto', label: 'Protesto', align: 'right', ordenarPor: null,
  },
  {
    campo: 'valorMulta', label: 'Multa', align: 'right', ordenarPor: null,
  },
  {
    campo: 'juros', label: 'Juros', align: 'right', ordenarPor: null,
  },
  {
    campo: 'valorHonorarios', label: 'Honorários', align: 'right', ordenarPor: null,
  },
  {
    campo: 'valorDesconto', label: 'Descontos', align: 'right', ordenarPor: null,
  },
  { campo: 'status', label: 'Status', ordenarPor: 'descricaoStatusTitulo' },
  { campo: 'acoes', label: 'Ações', ordenarPor: null },
];

/**
 * Valor de exibição de uma célula do corpo, indexado pelo mesmo `campo` de
 * `COLUNAS_PADRAO` — mantém header (`TableHead`) e corpo (`TableBody`) de
 * `TableTitulosProcesso.jsx` amarrados à mesma fonte de configuração
 * (`.map(COLUNAS_PADRAO)` nos dois), em vez de uma lista fixa de `TableCell`
 * cuja ordem precisaria ser mantida manualmente em paralelo (achado em code
 * review — mesmo padrão de bug já documentado para `MarcadoresCoresProcesso.jsx`
 * no CLAUDE.md do módulo: header/corpo desacoplados driftam sem erro de lint).
 *
 * Nunca chamada para `campo === 'acoes'` (AW2-1034 Etapa 4) — essa coluna
 * renderiza botões interativos, não um valor formatado; `TableTitulosProcesso.jsx`
 * trata esse `campo` à parte antes de chegar aqui.
 */
export function formatarValorColuna(campo, titulo) {
  if (CAMPOS_MONETARIOS.includes(campo)) {
    return titulo[campo] == null ? '—' : formatBRL(titulo[campo]);
  }

  if (campo === 'protestado') {
    return titulo.protestado ? 'Sim' : 'Não';
  }

  return titulo[campo];
}

/**
 * Mapeamento puro DTO real → shape de UI — espelha `buildTable`
 * (`Titulos.jsx` real), com 2 correções confirmadas por inspeção da
 * resposta crua do endpoint (`GET {CORE}/processos/{id}/titulos`,
 * AW2-1034 Etapa 1):
 *
 * 1. **Tipo usa `descricaoTipoTitulo`, não `idTipoTitulo`** — o DTO real
 *    tem os 2 campos distintos; `buildTable` da tela real usa o errado
 *    (`idTipoTitulo`, um código curto tipo "DP"). Bug confirmado, não
 *    replicado aqui.
 * 2. **`protestado`/`total` não existem no DTO** (confirmado — lista
 *    completa de campos documentada no plano de arquitetura) — calculados:
 *    `protestado = valorProtesto > 0`; `total` = soma dos valores que
 *    compõem o título menos o desconto, mesma fórmula do card "Cálculo do
 *    Processo" da mesma `AbaAcompanhamento.jsx`.
 *
 * Campos adicionais mantidos sem uso pela grid (AW2-1034 Etapa 4) — só para
 * pré-preencher `DialogTitulo` no modo Editar, mesmo motivo já documentado
 * para `idMotivoBaixa`/`isTituloEditavel`/`isTituloExcluivel` na Etapa 1:
 * `idTipoTitulo`/`idStatusTitulo` (os selectors "Tipo"/"Status" do dialog
 * precisam do id bruto, não da descrição já mapeada em `tipo`/`status`) e
 * `dataVencimentoIso` (o campo `<input type="date">` do dialog precisa do
 * ISO `YYYY-MM-DD` cru; `vencimento` já vem formatado `DD/MM/YYYY` para a
 * grid via `toBrazilianDate`, formato que o input nativo não aceita).
 */
export function mapearTitulos(content) {
  return (content || []).map((titulo) => {
    const total = (titulo.valorOriginal ?? 0)
      + (titulo.valorProtesto ?? 0)
      + (titulo.valorMulta ?? 0)
      + (titulo.valorJuros ?? 0)
      + (titulo.valorHonorarios ?? 0)
      - (titulo.valorDesconto ?? 0);

    return {
      id: titulo.idTitulo,
      sequencia: titulo.sequencia,
      status: titulo.descricaoStatusTitulo,
      idStatusTitulo: titulo.idStatusTitulo,
      tipo: titulo.descricaoTipoTitulo,
      idTipoTitulo: titulo.idTipoTitulo,
      numero: titulo.numero,
      vencimento: titulo.dataVencimento ? toBrazilianDate(titulo.dataVencimento) : null,
      // `new Date(...).toISOString().split('T')[0]` replica o mesmo 1º passo
      // de `toBrazilianDate` (ver `helpers/utils/utils.js`) — o DTO real
      // entrega `dataVencimento` como timestamp ISO completo, não
      // `YYYY-MM-DD` puro; o `<input type="date">` nativo exige o formato
      // puro.
      dataVencimentoIso: titulo.dataVencimento ? new Date(titulo.dataVencimento).toISOString().split('T')[0] : null,
      atraso: titulo.atraso,
      valorOriginal: titulo.valorOriginal,
      saldo: titulo.valorSaldoPrincipal,
      protestado: (titulo.valorProtesto ?? 0) > 0,
      valorProtesto: titulo.valorProtesto,
      juros: titulo.valorJuros,
      total,
      valorMulta: titulo.valorMulta,
      valorHonorarios: titulo.valorHonorarios,
      valorDesconto: titulo.valorDesconto,
      idMotivoBaixa: titulo.idMotivoBaixa,
      isTituloEditavel: titulo.isTituloEditavel,
      isTituloExcluivel: titulo.isTituloExcluivel,
      fazParteSimulacaoAcordoPropostaQualquer: titulo.fazParteSimulacaoAcordoPropostaQualquer,
    };
  });
}
